/**
 * DOM helpers for driving the config-editor via `data-ai-anchor` attributes. Kept pure (no React) so
 * they are unit-testable with jsdom and reusable by both the driver and the coach-mark overlay.
 */

const EDITABLE_PARTS = [
  'settings',
  'memory_size',
  'timeout',
  'snapstart',
  'env_vars',
  'lambda_handler',
  'lambda_source',
  'runtime',
  'architecture',
  // Not a field: the per-lambda Delete button. Recognized so highlight/expand get the same
  // tab-switch + settings fallback as field anchors. Only ever HIGHLIGHTED — a `click` on it is
  // rejected by the backend plan validator (CLICKABLE_ANCHORS), so the human stays the deleter.
  'delete',
] as const;

export type LambdaAnchorPart = (typeof EDITABLE_PARTS)[number];

export interface ParsedLambdaAnchor {
  name: string;
  part: LambdaAnchorPart;
}

/** CSS selector for a given anchor, safely escaped. */
export function anchorSelector(anchor: string): string {
  // CSS.escape is available in jsdom + all supported browsers.
  const escaped =
    typeof CSS !== 'undefined' && CSS.escape
      ? CSS.escape(anchor)
      : anchor.replace(/["\\]/g, '\\$&');
  return `[data-ai-anchor="${escaped}"]`;
}

/**
 * Logical scaffolder anchors resolve to the scaffolder wizard's footer buttons rendered by upstream
 * `@backstage/plugin-scaffolder-react` (the stock stepper, makeStyles name "BackstageTemplateStepper").
 * On every step the footer's forward/primary action is the SAME contained-primary button — Next on
 * early steps, Review on the last parameter step, Create on the review step — so `scaffolder.next`,
 * `scaffolder.review`, `scaffolder.create`, and the `scaffolder.wizard` readiness probe all map to it.
 * `scaffolder.back` maps to the footer's back button. This is a CSS fallback (the stock stepper exposes
 * no attribute seam); if upstream renames the class the anchor simply won't resolve and the coach-mark
 * degrades to a centered tooltip.
 */
// MUI v4 here emits JSS-suffixed class names (e.g. `MuiButton-containedPrimary-3177`), so the button
// class must be matched on a SUBSTRING — an exact `.MuiButton-containedPrimary` selector matches
// nothing and the whole scaffolder drive stalls (the readiness probe never resolves). Same reason the
// accordion helpers below use `[class*="Mui..."]`.
const STEPPER_PRIMARY_BTN =
  '[class*="BackstageTemplateStepper-footer"] [class*="MuiButton-containedPrimary"]';
const SCAFFOLDER_FALLBACK_SELECTORS: Record<string, string> = {
  'scaffolder.wizard': STEPPER_PRIMARY_BTN,
  'scaffolder.next': STEPPER_PRIMARY_BTN,
  'scaffolder.review': STEPPER_PRIMARY_BTN,
  'scaffolder.create': STEPPER_PRIMARY_BTN,
  'scaffolder.back': '[class*="BackstageTemplateStepper-backButton"]',
};

/**
 * Find the DOM node for an anchor. Prefers an explicit `data-ai-anchor` element (config-editor); falls
 * back to a known CSS selector for logical scaffolder anchors. Returns null if nothing matches.
 */
export function findAnchor(anchor: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const direct = document.querySelector<HTMLElement>(anchorSelector(anchor));
  if (direct) return direct;
  const fallback = SCAFFOLDER_FALLBACK_SELECTORS[anchor];
  return fallback ? document.querySelector<HTMLElement>(fallback) : null;
}

/**
 * Parse `lambda.<name>.settings` / `lambda.<name>.<field>`. The lambda name is greedy up to the last
 * dot so names containing dots still resolve (the suffix is one of a known, fixed set).
 */
export function parseLambdaAnchor(anchor: string): ParsedLambdaAnchor | null {
  const m = /^lambda\.(.+)\.([a-z_]+)$/.exec(anchor);
  if (!m) return null;
  const part = m[2] as LambdaAnchorPart;
  if (!EDITABLE_PARTS.includes(part)) return null;
  return { name: m[1], part };
}

/**
 * Resolve the best DOM element to point a coach-mark at. Falls back from a per-field lambda anchor
 * (e.g. `lambda.x.memory_size`) to the lambda's settings anchor when the field itself has no anchor,
 * so templates that only anchor the settings section still get a sensible highlight target.
 */
export function resolveAnchorElement(anchor: string | null): HTMLElement | null {
  if (!anchor) return null;
  const direct = findAnchor(anchor);
  if (direct) return direct;
  const parsed = parseLambdaAnchor(anchor);
  if (parsed && parsed.part !== 'settings') {
    return findAnchor(`lambda.${parsed.name}.settings`);
  }
  return null;
}

/**
 * Expand every collapsed MUI accordion in the element's ancestor chain (and the element itself, if it
 * is a collapsed summary). Config-editor fields live inside a "Runtime & Environment Settings"
 * accordion (collapsed by default), so revealing a field means expanding its section — and any section
 * nested above it. Outermost is clicked first so inner details are mounted before we expand them.
 */
/**
 * True only for MUI accordion summaries — either by the (JSS-suffixed) class, or as the direct
 * aria-expanded child of an accordion root. This deliberately EXCLUDES other `aria-expanded` widgets
 * (menus, selects, tree items) so we never toggle unrelated disclosures during a drive.
 */
function isAccordionSummary(node: HTMLElement): boolean {
  return (
    node.matches('[class*="MuiAccordionSummary-root"]') ||
    (node.hasAttribute('aria-expanded') &&
      !!node.parentElement?.matches('[class*="MuiAccordion-root"]'))
  );
}

function expandAccordions(el: HTMLElement): void {
  const toggles = new Set<HTMLElement>();
  // Only collapsed ACCORDION SUMMARIES are toggled — not any element with aria-expanded. MUI v4 here
  // uses suffixed JSS class names (`MuiAccordionSummary-root-1096`), so we match on a class substring
  // and, as a fallback, the aria-expanded child directly under an accordion root.
  const addIfCollapsed = (summary: HTMLElement | null) => {
    if (
      summary &&
      summary.getAttribute('aria-expanded') === 'false' &&
      isAccordionSummary(summary)
    ) {
      toggles.add(summary);
    }
  };
  // The anchor itself may be a collapsed summary (e.g. the "settings" section header).
  addIfCollapsed(el);
  // MUI renders Summary and Details as siblings under Accordion, so a field's collapsed section is
  // reached via its Accordion ancestors — expand each ancestor Accordion's own summary child. We do
  // NOT toggle arbitrary ancestors; only the summary under a matched accordion root.
  for (let node: HTMLElement | null = el.parentElement; node; node = node.parentElement) {
    if (node.matches('[class*="MuiAccordion-root"]')) {
      addIfCollapsed(node.querySelector<HTMLElement>(':scope > [aria-expanded]'));
    }
  }
  // Insertion order is innermost-first; click outermost first so inner details mount before expanding.
  Array.from(toggles)
    .reverse()
    .forEach(s => s.click());
}

/** Expand collapsed ancestors + scroll a resolved element to center (instant, not smooth, so the
 *  coach-mark Popper lands on a settled position rather than chasing an animating target). */
function reveal(el: HTMLElement): HTMLElement {
  expandAccordions(el);
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'center', inline: 'nearest' });
  }
  return el;
}

/**
 * Bring an anchor into view: expand its (and its ancestors') collapsed accordions, then scroll it to
 * center. Returns the element (or null if not in the DOM).
 */
export function revealAnchor(anchor: string): HTMLElement | null {
  const el = findAnchor(anchor);
  return el ? reveal(el) : null;
}

/**
 * revealAnchor with the coach-mark's fallback resolution: a missing lambda-scoped anchor (a field or
 * the delete button on a template that doesn't render it) reveals the lambda's settings section
 * instead of nothing — the same element the overlay will point at (resolveAnchorElement).
 */
export function revealResolvedAnchor(anchor: string): HTMLElement | null {
  const el = resolveAnchorElement(anchor);
  return el ? reveal(el) : null;
}
